<template>
  <div>
    <GmapMap
      :center="center"
      :zoom="zoom"
      ref="mapp"
      style="width: 100%; height: 400px"
      @click="mark"
    >
      <gmap-info-window
        :options="infoOptions"
        :position="infoWindowPos"
        :opened="infoWinOpen"
        @closeclick="infoWinOpen = false"
      >
      </gmap-info-window>
      <GmapMarker
        :key="index"
        v-for="(m, index) in markers"
        :position="m.position"
        :icon="getMarkerIcon(m, index)"
        :draggable="enableDragging"
        :animation="m.animation"
        @dragstart="handleMarkerDragStart($event, index)"
        @drag="handleMarkerDragging($event, index)"
        @dragend="handleMarkerDrag($event, index)"
        @click="handleMarkerClick(m, index)"
        @mouseover="handleMarkerMouseOver(index)"
        @mouseout="handleMarkerMouseOut(index)"
      />
      <gmap-polyline
          v-for="(p, index) in polylines"
          :path="p.data"
          :options="{ strokeColor: p.strokeColor, strokeWeight:5 }"></gmap-polyline>
    </GmapMap>
    <v-btn v-if="markers.length > 1" depressed color="success" @click="fitBounds" class="ma-1 float-right my-2">
      Fit
      <v-icon right dark>
        mdi-fit-to-screen-outline
      </v-icon>
    </v-btn>
  </div>
</template>
<script>
import { loadGmapApi, gmapApi } from "vue2-google-maps";

export default {
  props: {
    center: Object,
    zoom: Number,
    apiKey: String,
    markers: Array,
    selected: String,
    enabled: Boolean, // For map click to add stops
    enableDragging: {
      type: Boolean,
      default: false // For marker dragging - can be always true
    },
    polylines: []
  },
  data() {
    return {
      infoWindowPos: null,
      infoWinOpen: false,
      currentMidx: null,
      draggingMarkerIndex: null,
      hoverMarkerIndex: null,
      infoOptions: {
        content: "",
        //optional: offset infowindow so it visually sits nicely on top of our marker
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
    };
  },
  watch: {
    markers: function (newVal, oldVal) {
      this.fitBounds();
    },
    selected: function (newVal, oldVal) {
      let selectedMarkerIndex = -1;
      for (let index = 0; index < this.markers.length; index++) {
        const marker = this.markers[index];
        if (marker.place_id == newVal) {
          selectedMarkerIndex = index;
          break;
        }
      }
      if (selectedMarkerIndex != -1) {
        this.toggleInfoWindow(
          this.markers[selectedMarkerIndex],
          selectedMarkerIndex
        );
      } else {
        this.infoWinOpen = false;
      }
    },
  },
  computed: {
    google: gmapApi,
  },
  beforeMount() {
    if (this.google == null) {
      loadGmapApi({
        key: this.apiKey,
        libraries: "places, geocoding",
      });
    }
  },
  methods: {
    mark(event) {
      if(!this.enabled)
        return;

      const latlng = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };

      let geocoder = new google.maps.Geocoder();
      geocoder
        .geocode({ location: latlng })
        .then((response) => {
          if (response.results[0]) {
            this.$emit('map-click', response.results[0])
          } else {
            window.alert("No results found");
          }
        })
        .catch((e) => window.alert("Geocoder failed due to: " + e));
    },
    fitBounds() {
      if (this.markers.length == 0) return;
      
      if (this.markers.length == 1) {
        // For single marker, center on it with a good zoom level
        this.$refs.mapp.panTo(this.markers[0].position);
        this.$refs.mapp.setZoom(15);
        return;
      }

      // For multiple markers, fit to bounds
      var b = new google.maps.LatLngBounds();
      this.markers.forEach((marker) => {
        b.extend({
          lat: marker.position.lat,
          lng: marker.position.lng,
        });
      });
      
      // Add padding around bounds for better visibility
      this.$refs.mapp.fitBounds(b, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
    },
    toggleInfoWindow: function (marker, idx) {
      this.infoWindowPos = marker.position;
      this.infoOptions.content = marker.infoText;

      //check if its the same marker that was selected if yes toggle
      if (this.currentMidx == idx) {
        this.infoWinOpen = !this.infoWinOpen;
      }
      //if different marker set infowindow to open and reset current marker index
      else {
        this.infoWinOpen = true;
        this.currentMidx = idx;
      }
    },
    getMarkerIcon(marker, index) {
      // Return custom icon with visual effects based on state
      if (this.draggingMarkerIndex === index) {
        // Marker being dragged - enhanced visual
        return {
          url: marker.icon?.url || 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new google.maps.Size(40, 40), // Larger when dragging
          anchor: new google.maps.Point(20, 40),
        };
      } else if (this.hoverMarkerIndex === index && this.enableDragging) {
        // Marker being hovered and dragging is enabled - slightly larger
        return {
          url: marker.icon?.url || 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new google.maps.Size(35, 35),
          anchor: new google.maps.Point(17.5, 35),
        };
      } else if (marker.icon) {
        return marker.icon;
      }
      return null;
    },
    handleMarkerDragStart(event, index) {
      this.draggingMarkerIndex = index;
      this.infoWinOpen = false;
      this.$emit('marker-drag-start', { index });
    },
    handleMarkerDragging(event, index) {
      // Called continuously while dragging
      this.$emit('marker-dragging', { 
        index, 
        position: {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
      });
    },
    handleMarkerDrag(event, index) {
      this.draggingMarkerIndex = null;
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      this.$emit('marker-dragged', { index, position: newPosition });
    },
    handleMarkerClick(marker, index) {
      if (this.draggingMarkerIndex === null) {
        this.toggleInfoWindow(marker, index);
        this.$emit('marker-clicked', { index, marker });
      }
    },
    handleMarkerMouseOver(index) {
      if (this.enableDragging && this.draggingMarkerIndex === null) {
        this.hoverMarkerIndex = index;
        // Change cursor to grab hand
        document.body.style.cursor = 'grab';
      }
    },
    handleMarkerMouseOut(index) {
      this.hoverMarkerIndex = null;
      document.body.style.cursor = 'default';
    },
  },
};
</script>
<style scoped>
/* Add smooth transitions for map interactions */
.vue-map-container {
  transition: all 0.3s ease;
}

/* Cursor styles for draggable markers */
::v-deep .gm-style > div > div > div > div > img {
  transition: transform 0.2s ease, filter 0.2s ease;
}

::v-deep .gm-style > div > div > div > div > img:hover {
  transform: scale(1.1);
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
}
</style>
